import React, { useState } from 'react';
import { Search, Calendar, Phone, Hash, Globe, Smartphone, ChevronRight, Filter } from 'lucide-react';
import { Order, OrderStatus } from './OrderQueue';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderHistoryProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
export function OrderHistory({
  orders,
  onSelectOrder
}: OrderHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const filteredOrders = orders.filter(order => {
    const isTerminalStatus = ['Delivered', 'Cancelled', 'Rejected', 'Return Requested', 'Refunded'].includes(order.status);
    if (!isTerminalStatus) return false;
    const matchesId = order.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customerPhone?.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    return matchesId && matchesPhone && matchesSource;
  });
  return <div className="space-y-6" data-id="element-459">
      <div className="flex items-center justify-between" data-id="element-460">
        <div data-id="element-461">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-462">Order History</h2>
          <p className="text-slate-500" data-id="element-463">Search and review past order logs</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-4 gap-4" data-id="element-464">
        <div className="space-y-2" data-id="element-465">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-466">
            <Hash className="w-3 h-3" data-id="element-467" />
            Order ID
          </label>
          <div className="relative" data-id="element-468">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-469" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-470" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-471">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-472">
            <Phone className="w-3 h-3" data-id="element-473" />
            Customer Phone
          </label>
          <div className="relative" data-id="element-474">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-475" />
            <input type="text" value={phoneQuery} onChange={e => setPhoneQuery(e.target.value)} placeholder="050..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-476" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-477">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-478">
            <Globe className="w-3 h-3" data-id="element-479" />
            Source
          </label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2 appearance-none" data-id="element-480">
            <option value="all" data-id="element-481">All Sources</option>
            <option value="Website" data-id="element-482">Website</option>
            <option value="Aggregator" data-id="element-483">Aggregator</option>
          </select>
        </div>

        <div className="space-y-2" data-id="element-484">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-485">
            <Calendar className="w-3 h-3" data-id="element-486" />
            Date
          </label>
          <input type="date" className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-487" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-488">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-489">
          <div data-id="element-490">Order ID</div>
          <div data-id="element-491">Customer</div>
          <div data-id="element-492">Source</div>
          <div data-id="element-493">Status</div>
          <div data-id="element-494">Date</div>
          <div data-id="element-495">Amount</div>
          <div className="w-10" data-id="element-496"></div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-497">
          {filteredOrders.map(order => <button key={order.id} onClick={() => onSelectOrder(order)} className="w-full grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-5 items-center hover:bg-slate-50 transition-colors text-left group" data-id="element-498">
              <div className="font-mono font-bold text-blue-600" data-id="element-499">
                #{order.id}
              </div>

              <div data-id="element-500">
                <p className="font-semibold text-slate-900" data-id="element-501">
                  {order.customerName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-502">{order.customerPhone}</p>
              </div>

              <div className="flex items-center gap-2" data-id="element-503">
                {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-504">
                    <Globe className="w-3 h-3" data-id="element-505" />
                    Website
                  </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-506">
                    <Smartphone className="w-3 h-3" data-id="element-507" />
                    Aggregator
                  </div>}
              </div>

              <div data-id="element-508">
                <StatusBadge status={order.status} data-id="element-509" />
              </div>

              <div className="text-sm text-slate-600 font-medium" data-id="element-510">
                {order.createdAt}
              </div>

              <div data-id="element-511">
                <p className="font-bold text-slate-900" data-id="element-512">
                  {order.totalAmount.toFixed(2)} SAR
                </p>
                <p className={cn('text-[10px] font-bold uppercase', order.paymentType === 'COD' ? 'text-amber-600' : 'text-emerald-600')} data-id="element-513">
                  {order.paymentType}
                </p>
              </div>

              <div className="flex items-center gap-3" data-id="element-514">
                {order.status === 'Delivered' && <button onClick={e => {
              e.stopPropagation();
              import('sonner').then(({
                toast
              }) => toast.info('Return request initiated for #' + order.id));
            }} className="px-3 py-1 bg-rose-50 text-rose-600 rounded-lg text-[10px] font-bold uppercase border border-rose-100 hover:bg-rose-100 transition-colors" data-id="element-515">
                    Return
                  </button>}
                <div className="text-slate-300 group-hover:text-blue-600 transition-colors" data-id="element-516">
                  <ChevronRight className="w-5 h-5" data-id="element-517" />
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<string, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100'
  };
  return <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border', styles[status] || 'bg-slate-50 text-slate-700 border-slate-100')} data-id="element-518">
      {status}
    </div>;
}